import { useI18n } from '../lib/i18n';
import type { Period } from '../lib/period';
import { Badge, Table, Td, Th, Tr, type BadgeTone } from './ui';
import { useNumber } from './kit';
import { StatusBadge, TextLink, useAmount, useCode, useDates } from './b-shared';

/**
 * What Projects & Clients and their detail pages share (CR-008 §9): a project's row, its delivery tier and
 * reasons, its progress as tracked tasks — never a percentage of the project — and Finance's figures for it,
 * "Unknown" where a currency has no rate, never a false zero.
 */

export interface ProjectMoney {
  invoiced: number | null;
  collected: number;
  outstanding: number | null;
  currency: string;
  unconverted: string[];
}

export type RiskTier = 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';

export interface ProjectRow {
  project: {
    id: string;
    code: string;
    name: string;
    nameAr: string | null;
    status: string;
    priority: string;
    startDate: string | null;
    expectedEndDate: string | null;
    actualEndDate: string | null;
  };
  client: { id: string; code: string; companyName: string; companyNameAr: string | null; status: string } | null;
  internal: boolean;
  owner: { id: string; fullName: string; fullNameAr: string | null } | null;
  support: { contractId: string; number: string; until: string } | null;
  tasks: { tracked: number; done: number; open: number; blocked: number; review: number; overdue: number; openWithDue: number };
  progress: { done: number; tracked: number };
  tickets: { open: number; resolvedInPeriod: number };
  risk: { tier: RiskTier; reasons: string[] };
  money: ProjectMoney | null;
}

export interface ClientRow {
  client: { id: string; code: string; companyName: string; companyNameAr: string | null; status: string; countryCode: string; defaultCurrency: string };
  projects: { total: number; delivering: number };
  tickets: { open: number; resolvedInPeriod: number; reopenedInPeriod: number };
  service: { assignmentSlaPercent: number | null; viewSlaPercent: number | null; decided: number };
  money: ProjectMoney & { collectedInPeriod: number; overdueInvoices: number };
  contracts: { covering: number; endingSoon: number; endingSoonDays: number };
}

export interface WithPeriod {
  period: Period;
}

const RISK_TONE: Record<RiskTier, BadgeTone> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'success', NONE: 'neutral' };
export const RISK_TIERS: RiskTier[] = ['HIGH', 'MEDIUM', 'LOW', 'NONE'];

/** The delivery tier, and its reasons in words. */
export function RiskBadge({ risk, withReasons = true }: { risk: ProjectRow['risk']; withReasons?: boolean }) {
  const { t } = useI18n();
  const code = useCode();
  return (
    <span className="flex flex-col items-start gap-1">
      <Badge tone={RISK_TONE[risk.tier] ?? 'neutral'}>{t(`projects.risk.${risk.tier}`)}</Badge>
      {withReasons && risk.reasons.length > 0 && (
        <span className="text-caption text-[var(--text-tertiary)]">{risk.reasons.map((r) => code('projects.reason', r)).join(' · ')}</span>
      )}
    </span>
  );
}

/** "X of Y tracked tasks complete" — a count of tasks, never "the project is X% done". */
export function ProgressText({ progress }: { progress: ProjectRow['progress'] }) {
  const { t } = useI18n();
  const num = useNumber();
  if (progress.tracked === 0) return <span className="text-muted">{t('projects.noTrackedTasks')}</span>;
  return <span>{t('projects.progress', { done: num(progress.done), tracked: num(progress.tracked) })}</span>;
}

/** One of a project's money figures: "Unknown" where a currency has no rate; nothing invoiced said in words. */
export function MoneyCell({ money, field }: { money: ProjectMoney | null; field: 'invoiced' | 'collected' | 'outstanding' }) {
  const { t } = useI18n();
  const amount = useAmount();
  if (!money) return <span className="text-caption text-muted">{t('projects.noInvoices')}</span>;
  const value = money[field];
  if (value === null) {
    return (
      <span className="flex flex-col items-end">
        <span className="text-warning-fg dark:text-warning-dark">{t('common.unknown')}</span>
        {money.unconverted.length > 0 && <span className="text-caption text-muted">{t('projects.noRate', { currencies: money.unconverted.join(', ') })}</span>}
      </span>
    );
  }
  return <span className="tabular">{amount(value, money.currency)}</span>;
}

/** The projects table — on Projects & Clients, and a client's own projects. */
export function ProjectsTable({ rows, query, showClient = true }: { rows: ProjectRow[]; query: string; showClient?: boolean }) {
  const { t, localized } = useI18n();
  const num = useNumber();
  const dates = useDates();
  const search = query ? `?${query}` : '';
  return (
    <Table>
      <thead>
        <tr>
          <Th>{t('common.project')}</Th>
          {showClient && <Th>{t('common.client')}</Th>}
          <Th>{t('common.status')}</Th>
          <Th>{t('projects.col.risk')}</Th>
          <Th>{t('projects.col.progress')}</Th>
          <Th align="end">{t('projects.col.overdueBlocked')}</Th>
          <Th align="end">{t('projects.col.openTickets')}</Th>
          <Th>{t('projects.col.support')}</Th>
          <Th align="end">{t('projects.col.invoiced')}</Th>
          <Th align="end">{t('projects.col.collected')}</Th>
          <Th align="end">{t('projects.col.outstanding')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Tr key={r.project.id}>
            <Td className="min-w-[13rem]">
              <TextLink to={`/projects/${r.project.id}${search}`} className="font-medium">
                {localized(r.project)}
              </TextLink>
              <span dir="ltr" className="block font-mono text-caption text-[var(--text-tertiary)]">
                {r.project.code}
              </span>
            </Td>
            {showClient && (
              <Td className="min-w-[9rem]">
                {r.client ? <TextLink to={`/clients/${r.client.id}${search}`}>{localized(r.client)}</TextLink> : <Badge tone="neutral">{t('common.internal')}</Badge>}
              </Td>
            )}
            <Td>
              <StatusBadge status={r.project.status} />
            </Td>
            <Td className="min-w-[9rem]">
              <RiskBadge risk={r.risk} />
            </Td>
            <Td className="min-w-[11rem] text-[var(--text-secondary)]">
              <ProgressText progress={r.progress} />
            </Td>
            <Td align="end" className="whitespace-nowrap tabular">
              <span className={r.tasks.overdue > 0 ? 'text-error-fg dark:text-error-dark' : undefined}>{num(r.tasks.overdue)}</span>
              <span className="text-[var(--text-tertiary)]"> · </span>
              <span className={r.tasks.blocked > 0 ? 'text-warning-fg dark:text-warning-dark' : undefined}>{num(r.tasks.blocked)}</span>
            </Td>
            <Td align="end" className="tabular">
              {num(r.tickets.open)}
            </Td>
            <Td className="min-w-[9rem]">
              {r.support ? (
                <span className="flex flex-col">
                  <span dir="ltr" className="font-mono text-caption">
                    {r.support.number}
                  </span>
                  <span className="text-caption text-muted">{t('projects.supportUntil', { date: dates.day(r.support.until) })}</span>
                </span>
              ) : (
                <span className="text-caption text-muted">{t('projects.noSupport')}</span>
              )}
            </Td>
            <Td align="end">
              <MoneyCell money={r.money} field="invoiced" />
            </Td>
            <Td align="end">
              <MoneyCell money={r.money} field="collected" />
            </Td>
            <Td align="end">
              <MoneyCell money={r.money} field="outstanding" />
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}
